import { memo, type CSSProperties } from 'react';

import './ggl_spinner.scss';

/** Цвет кольца спиннера. */
export type GglSpinnerColor = 'primary' | 'white' | 'multi' | 'two';

/** Размер спиннера: именованный пресет либо явное число в пикселях. */
export type GglSpinnerSize = 'sm' | 'md' | 'lg' | number;

/** Карта именованных размеров → px. */
const SIZE_PX: Record<Exclude<GglSpinnerSize, number>, number> = {
  sm: 24,
  md: 40,
  lg: 65,
};

export interface GglSpinnerProps {
  /**
   * Цвет кольца. `primary` ($primary, монохром) — по умолчанию.
   * `white` — для тёмного фона; `multi` — фирменная переливка для брандовых
   * экранов (не по умолчанию); `two` — двухтоновая (legacy).
   */
  color?: GglSpinnerColor;
  /**
   * Размер: `'sm'`=24px, `'md'`=40px, `'lg'`=65px (по умолчанию) либо число в px.
   * Legacy-пропс `width` при передаче перекрывает `size`.
   */
  size?: GglSpinnerSize;
  /**
   * Legacy-ширина в px (напр. `'65'`). Если передан — перекрывает `size`.
   * Оставлен для обратной совместимости с существующими потребителями.
   */
  width?: string | number;
  /** Подпись рядом со спиннером (напр. «Загрузка…»). По умолчанию нет. */
  label?: string;
  /** Центрировать спиннер в блоке с минимальной высотой `outerHeight`. */
  centerBlock?: boolean;
  /** Расположить спиннер абсолютным слоем на 100% родителя (поверх контента). */
  overlay?: boolean;
  /** Минимальная высота контейнера при `centerBlock` (напр. `'350px'`). */
  outerHeight?: string;
}

/** Размер в px: legacy `width` имеет приоритет над `size`. */
function resolveSize(size: GglSpinnerSize, width: string | number | undefined): number {
  if (width !== undefined && width !== '') {
    const parsed = parseInt(String(width), 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return typeof size === 'number' ? size : SIZE_PX[size];
}

const GglSpinner = ({
  color = 'primary',
  size = 'lg',
  width,
  label,
  centerBlock = false,
  overlay = false,
  outerHeight,
}: GglSpinnerProps) => {
  const sizePx = resolveSize(size, width);

  // Динамическая мин. высота блока передаётся в SCSS через CSS-переменную —
  // единственный style-атрибут: значение зависит от пропса outerHeight и не
  // может быть статической утилитой/классом. Каст нужен, т.к. у CSSProperties
  // намеренно нет индекс-сигнатуры для кастомных свойств (см. @types/react).
  const wrapperStyle = outerHeight
    ? ({ '--ggl-spinner-min-h': outerHeight } as CSSProperties)
    : undefined;

  const spinner = (
    <>
      <svg
        className='ggl-spinner mx-2'
        width={ sizePx }
        height={ sizePx }
        viewBox='0 0 66 66'
        xmlns='http://www.w3.org/2000/svg'
        role='img'
        aria-label={ label || 'Загрузка' }
      >
        <circle
          className={ `ggl-path-${ color }` }
          fill='none'
          strokeWidth='6'
          strokeLinecap='round'
          cx='33'
          cy='33'
          r='30'
        />
      </svg>
      { label && <span className='small text-muted'>{ label }</span> }
    </>
  );

  if (overlay) {
    return <div className='ggl-spinner-hr-center ggl-spinner-overlay'>{ spinner }</div>;
  }

  if (centerBlock) {
    return (
      <div className='ggl-spinner-hr-center' style={ wrapperStyle }>
        { spinner }
      </div>
    );
  }

  return spinner;
};

GglSpinner.displayName = 'GglSpinner';

export default memo(GglSpinner);
